/**
 * Framework Readiness Widget — evidence-collection KPIs per active framework.
 *
 * Data comes from GET /api/grc/auto-evidence/readiness/summary which joins:
 *   - the tenant's Active Frameworks (same list as /grc/frameworks)
 *   - the nightly-refreshed control_evidence_cache
 *   - open High/Critical Amidala CSPM findings mapped to each framework
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, RefreshCcw, Layers, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/services/api';

const LEVEL_COLOR = (pct) => {
  if (pct >= 80) return 'text-emerald-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-red-600';
};

export default function FrameworkReadinessWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/grc/auto-evidence/readiness/summary');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load readiness summary');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      // Kick a refresh for every active framework, then reload the summary
      const codes = (data?.frameworks || []).map((f) => f.code);
      await Promise.all(
        codes.map((code) =>
          api.post('/grc/auto-evidence/refresh', { framework: code }).catch(() => null)
        )
      );
      await load();
      toast.success('Evidence cache refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card data-testid="readiness-widget">
        <CardContent className="py-10 flex items-center justify-center text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading framework readiness…</span>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const frameworks = data?.frameworks || [];

  return (
    <Card data-testid="readiness-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-600" />
            Framework Readiness — Evidence Collection
            <Badge variant="outline" className="text-[10px]" data-testid="readiness-framework-count">
              {summary.active_frameworks || 0} active
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={refresh}
              disabled={refreshing || frameworks.length === 0}
              data-testid="readiness-refresh-btn"
            >
              <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/grc/auto-evidence')}
              data-testid="readiness-view-all-btn"
            >
              View details <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {frameworks.length === 0 ? (
          <div className="px-6 py-10 text-center text-muted-foreground" data-testid="readiness-empty">
            <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No active frameworks yet</p>
            <p className="text-xs mt-1">Enable a framework under Frameworks to start tracking evidence readiness.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {frameworks.map((fw) => (
              <div
                key={fw.code}
                className="px-5 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => navigate('/grc/auto-evidence')}
                data-testid={`readiness-row-${fw.code}`}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-[180px] flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{fw.name}</span>
                      {fw.open_cspm_critical > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] h-5" data-testid={`readiness-critical-${fw.code}`}>
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          {fw.open_cspm_critical} open High/Crit
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`readiness-counts-${fw.code}`}>
                      {fw.controls_ready} of {fw.controls_total} controls have evidence
                      {fw.controls_missing > 0 && ` · ${fw.controls_missing} missing`}
                    </p>
                  </div>
                  <div className="w-40">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-muted-foreground">Avg coverage</span>
                      <span className={`text-xs font-bold ${LEVEL_COLOR(fw.coverage_pct)}`} data-testid={`readiness-pct-${fw.code}`}>
                        {fw.coverage_pct}%
                      </span>
                    </div>
                    <Progress value={fw.coverage_pct} className="h-1.5" />
                  </div>
                  <div className="text-right min-w-[90px]">
                    {fw.last_refreshed_at ? (
                      <p className="text-[11px] text-muted-foreground">
                        Updated {new Date(fw.last_refreshed_at).toLocaleDateString()}
                      </p>
                    ) : (
                      <p className="text-[11px] text-amber-600 flex items-center justify-end gap-1">
                        <ShieldAlert className="w-3 h-3" /> never synced
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {frameworks.length > 0 && (
          <div className="px-5 py-3 bg-muted/30 border-t flex items-center justify-between" data-testid="readiness-overall">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Overall: <strong>{summary.controls_ready || 0} / {summary.controls_total || 0}</strong> controls with evidence ·
              avg coverage <strong>{summary.avg_coverage_pct || 0}%</strong>
            </div>
            {(summary.open_cspm_critical || 0) > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] h-5">
                {summary.open_cspm_critical} open High/Critical findings across frameworks
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
